import { GameState, Facing, InputState, AABB } from '../types';
import { PLAYER_SPEED, PLAYER_SIZE, CHOP_RANGE, CHOP_HITS, TILE, THROW_COOLDOWN, SPEAR_SPEED, SPEAR_LIFETIME, WOOD_PER_TREE, VENDOR_RANGE, WOOD_SELL_PRICE, UPGRADE_RANGE } from '../constants';
import { moveAndSlide, getSolidAABBs, dist } from '../collision';
import { spawnChopParticles, spawnCoinParticles } from '../particles';
import { playChopSound, playThrowSound, playSellSound } from '../audio';

export function updatePlayer(state: GameState, input: InputState, dt: number): void {
  const p = state.player;

  // Cooldowns
  if (p.chopCooldown > 0) p.chopCooldown -= dt;
  if (p.throwCooldown > 0) p.throwCooldown -= dt;
  if (p.chopTimer > 0) {
    p.chopTimer -= dt;
    if (p.chopTimer <= 0) p.isChopping = false;
  }

  // Movement
  let mx = 0, my = 0;
  if (input.up) my -= 1;
  if (input.down) my += 1;
  if (input.left) mx -= 1;
  if (input.right) mx += 1;

  // Normalize diagonal
  if (mx !== 0 && my !== 0) {
    const inv = 1 / Math.SQRT2;
    mx *= inv;
    my *= inv;
  }

  // Facing
  if (mx !== 0 || my !== 0) {
    if (Math.abs(mx) >= Math.abs(my)) {
      p.facing = mx > 0 ? Facing.Right : Facing.Left;
    } else {
      p.facing = my > 0 ? Facing.Down : Facing.Up;
    }
  }

  const speed = PLAYER_SPEED * dt;
  const solids = getSolidAABBs(state);
  const result = moveAndSlide(p.x, p.y, mx * speed, my * speed, PLAYER_SIZE, solids);
  p.x = result.x;
  p.y = result.y;

  // Chopping
  if (input.chop && p.chopCooldown <= 0) {
    tryChop(state);
    p.chopCooldown = 0.3;
    p.isChopping = true;
    p.chopTimer = 0.2;
  }

  // Throwing spear
  if (input.throw && p.throwCooldown <= 0 && p.spears > 0) {
    throwSpear(state);
    p.throwCooldown = THROW_COOLDOWN;
    p.spears--;
  }

  // Interact (sell at vendor)
  if (input.interact) {
    trySellAtVendor(state);
    tryInteractUpgradeStation(state);
  }
}

function tryChop(state: GameState): void {
  const p = state.player;
  const facingOffset = getFacingOffset(p.facing);
  const targetX = p.x + facingOffset.x * 40;
  const targetY = p.y + facingOffset.y * 40;

  let nearest: { tree: typeof state.trees[0]; d: number } | null = null;
  for (const tree of state.trees) {
    if (tree.hp <= 0) continue;
    const treeCx = tree.gridX * TILE + TILE / 2;
    const treeCy = tree.gridY * TILE + TILE / 2;
    const d = dist(targetX, targetY, treeCx, treeCy);
    if (d < CHOP_RANGE && (!nearest || d < nearest.d)) {
      nearest = { tree, d };
    }
  }

  if (nearest) {
    const tree = nearest.tree;
    const hitsNeeded = state.player.hasSuperAxe ? 1 : 1;
    tree.hp -= (state.player.hasSuperAxe ? CHOP_HITS : 1);
    tree.shakeTimer = 0.2;

    const treeCx = tree.gridX * TILE + TILE / 2;
    const treeCy = tree.gridY * TILE + TILE / 2;
    spawnChopParticles(treeCx, treeCy);
    playChopSound();

    if (tree.hp <= 0) {
      tree.hp = 0;
      tree.regrowTimer = 20;
      state.player.wood += WOOD_PER_TREE;
      spawnCoinParticles(treeCx, treeCy - 10);
    }
  }
}

function throwSpear(state: GameState): void {
  const p = state.player;
  const offset = getFacingOffset(p.facing);
  state.projectiles.push({
    x: p.x + offset.x * 20,
    y: p.y + offset.y * 20,
    dx: offset.x * SPEAR_SPEED,
    dy: offset.y * SPEAR_SPEED,
    lifetime: SPEAR_LIFETIME,
  });
  playThrowSound();
}

function trySellAtVendor(state: GameState): void {
  const vx = state.vendorX * TILE + TILE / 2;
  const vy = state.vendorY * TILE + TILE / 2;
  const d = dist(state.player.x, state.player.y, vx, vy);
  if (d < VENDOR_RANGE && state.player.wood > 0) {
    const earned = state.player.wood * WOOD_SELL_PRICE;
    state.player.coins += earned;
    spawnCoinParticles(vx, vy - 20);
    playSellSound();
    state.player.wood = 0;
  }
}

function tryInteractUpgradeStation(state: GameState): void {
  const sx = state.stationGridX * TILE + TILE / 2;
  const sy = state.stationGridY * TILE + TILE / 2;
  const d = dist(state.player.x, state.player.y, sx, sy);
  if (d < UPGRADE_RANGE) {
    // Show upgrade modal - handled by UI
    const modal = document.getElementById('upgrade-modal');
    if (modal) modal.classList.remove('hidden');
  }
}

function getFacingOffset(facing: Facing): { x: number; y: number } {
  switch (facing) {
    case Facing.Up: return { x: 0, y: -1 };
    case Facing.Down: return { x: 0, y: 1 };
    case Facing.Left: return { x: -1, y: 0 };
    case Facing.Right: return { x: 1, y: 0 };
  }
}
